import React, { useContext, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Box,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { Link as RouterLink } from "react-router-dom";
import currency from "currency.js";
import { UserContext } from "../../context/UserContext";
import { hasManagerAccess, capitalize, isMultiModalPaymentAvailable } from "../../common/util";
import OrderRefund from "../screen/OrderRefund";
import Transfers from "./Transfers";
import moment from "moment";
import ShowMore from "../../common/component/ShowMore";
import { STORAGE_BUCKET } from "../../common/constants";
import BalancePaymentDate from "./BalancePaymentDate";
import ShareButton from "../../common/component/ShareButton";
import CustomerPayment from "./CustomerPayment";
import CollectBalancePayment from "./CollectBalancePayment";
import { ExpandMoreOutlined } from "@mui/icons-material";

export default function BakerOrderPayment(props) {
  const { order, onPaymentSuccess } = props;
  const { user } = useContext(UserContext);
  const [paymentOpen, setPaymentOpen] = useState(false);

  let isManager = hasManagerAccess(user.role) || user.role === "kitchen leader";

  let dueDate = moment(order.fulfillment.date).subtract(7, "days");
  if (order.payment.dueDate) {
    dueDate = moment(order.payment.dueDate);
  }

  let showRefundButton =
    isManager &&
    order.src === "direct" &&
    order.paymentType !== "offline" &&
    (order.status === "fulfilled" || order.status === "completed" || order.status === "canceled");

  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={8}>
          <Typography variant="overline" color="textSecondary">
            Payment Details
          </Typography>
        </Grid>
        <Grid item xs={4} align="right">
          {order.useTxnLedger && (
            <Tooltip title="Transactions">
              <IconButton size="small" component={RouterLink} to={`/order/${order.id}/transaction`}>
                <ReceiptLongOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          {isManager && order.src === "direct" && order.paymentType !== "offline" && (
            <Transfers data={order.transfers} />
          )}
          {+order.payment.due > 0 &&
            isManager &&
            order.src === "direct" &&
            order.paymentType !== "offline" && (
              <ShareButton
                type="balance payment page for this order"
                link={`/order/${order.id}/balance-payment`}
              />
            )}
          {showRefundButton && <OrderRefund order={order} />}
        </Grid>
      </Grid>
      <Paper elevation={0}>
        <Box pt={1}>
          <Box mb={2} sx={{ borderBottom: "1px solid #ececec" }}>
            <Accordion elevation={0} sx={{ padding: 0 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreOutlined />}
                sx={{ padding: 0, paddingLeft: 2 }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="overline">Total</Typography>
                  </Grid>
                  <Grid item xs={6} align="right">
                    <Typography variant="overline">
                      {currency(order.payment.total).format()}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <Box py={2} pl={2} pr={3} sx={{ borderTop: "1px solid #ececec" }}>
                  <CustomerPayment payment={order.payment} />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
          {order.src === "direct" && (
            <Box pl={2} pr={3} pb={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="overline" color="textSecondary">
                    Amount Paid
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="overline" color="textSecondary">
                    {currency(order.payment.paid).format()}
                  </Typography>
                </Grid>
              </Grid>
              {order.src === "direct" && order.payment.refund && (
                <Grid container>
                  <Grid item xs={6}>
                    <ShowMore openCta="Refund" closeCta="Refund">
                      {order.payment.refund.map((o) => (
                        <Box>
                          <Typography variant="overline" display="block">
                            {currency(o.amount).format()} Refund
                          </Typography>
                          <Box my={0.25}>
                            <Typography variant="body2" display="block" color="textSecondary">
                              Reason: {capitalize(o.reason)}. {o.note}
                            </Typography>
                          </Box>
                          <Box my={0.25}>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {moment(o.ts).format("LLL")}
                            </Typography>
                          </Box>
                          <Avatar src={`${STORAGE_BUCKET}/img/u/${o.uid}.webp`}></Avatar>
                        </Box>
                      ))}
                    </ShowMore>
                  </Grid>
                  <Grid item xs={6} align="right">
                    <Box pt={1}>
                      <Typography variant="overline" color="error">
                        {currency(
                          order.payment.refund.reduce((acc, obj) => {
                            return +acc + +obj.amount;
                          }, 0)
                        ).format()}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
              {order.status !== "canceled" && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="textSecondary">
                        Balance Due {+order.payment.due > 0 && <BalancePaymentDate order={order} />}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="overline" color="textSecondary">
                        {currency(order.payment.due).format()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>
      {order.transferId && (
        <Box my={2} textAlign={"center"}>
          <Typography variant="caption" color="textSecondary" display="block">
            BakeStreet sent your payment on {moment(order.transferTs).format("LLLL")}
          </Typography>
        </Box>
      )}
      {(isMultiModalPaymentAvailable(user, order) || paymentOpen) && (
        <Box mt={1}>
          <CollectBalancePayment
            orderId={order.id}
            onPaymentSuccess={onPaymentSuccess}
            onOpenChange={setPaymentOpen}
          />
        </Box>
      )}
    </Box>
  );
}
